import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { BlogNewHeader } from '@/components/blog/BlogPageHeaders';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useGDriveChannels, useGDriveFolders, friendlyGDriveImportErrorMessage, GDRIVE_KEYS } from '@/hooks/useGDriveImport';
import { useGDrivePicker } from '@/hooks/useGDrivePicker';
import { useLockedEntry } from '@/hooks/useLockedEntry';
import { GDriveFolderPickerPanel } from '@/components/gdrive/GDriveFolderPickerPanel';
import { useWordPressChannels } from '@/hooks/useWordPress';
import { usePullBlogPost } from '@/hooks/useBlog';
import { useAuth } from '@/hooks/useAuth';
import { formatApiError, isGlobalGatingHandledError } from '@/services/api';
import { toast } from '@/utils/toast';
import { SiteFavicon } from '@/components/blog/SiteFavicon';
import { channelsApi } from '@/services/channels';
import type { GDrivePickerFile } from '@/types/gdrive';

/** Route this page is mounted at (see App.tsx) — used as the OAuth `returnTo`
 * so an inline "Connect Google Drive" click on this page returns HERE
 * instead of the default `/settings/channels` (cdb #280). */
const BLOG_NEW_ROUTE = '/blog/new';

/**
 * Backend code for "the connected Google account's stored token can't read
 * this Doc" — thrown by `gdocsService.fetchDocument`'s `translateDocsError`
 * for BOTH a revoked/expired token (401) and a not-shared-with-this-account
 * permission error (403). See `backend/src/types/gdocs.types.ts`
 * `GDocsAccessDeniedError` ('GDOCS_ACCESS_DENIED'). The common real-world
 * trigger is the Picker/account-mismatch: the user signed into a DIFFERENT
 * Google account in the browser Picker than the one the Drive channel is
 * connected as, so the doc they picked isn't accessible to the connected
 * (server-side) token used for the actual pull.
 */
const GDOCS_ACCESS_DENIED_CODE = 'GDOCS_ACCESS_DENIED';

/**
 * "New Blog Post" — pull step (SPEC.md Phase 1).
 *
 * The Google Picker is the ONLY way to select the source Google Doc.
 * Reading a Doc by pasted URL/ID would require the sensitive
 * `documents.readonly` scope, which is gated behind Google app-verification
 * (works only for test users until approved). The Picker uses the
 * non-sensitive `drive.file` scope instead — a browser-obtained,
 * short-lived OAuth access token via Google Identity Services (never the
 * team's server-side stored GDrive refresh token) — so it works for every
 * user immediately. See `useGDrivePicker`. Restricted to native Google Docs
 * via `acceptMimeTypes`.
 *
 * Requires the team to have a connected Google Drive channel — the pull
 * itself is also gated server-side, but we show a clear "connect first"
 * state instead of letting the user hit a confusing 400.
 *
 * Folder scoping (docs/features/gdrive-folder-scoping/SPEC.md, ACCESS-MODE
 * extension) — mirrors the fail-closed flow in `MediaSection.tsx`. Always
 * branches on `mode`, never on `folders.length`:
 *   - `'scoped'` — the Picker is locked to the channel's `allowedFolders`.
 *   - `'all'` — the Picker opens unrestricted (whole Drive, still filtered
 *     to native Google Docs via `acceptMimeTypes`).
 *   - `'unconfigured'` — the Picker never opens; the connector sees the
 *     "Configure shared folders" prompt (now offering both "Specific
 *     folders" and "Entire Drive"), everyone else sees a "waiting for
 *     configuration" message.
 * The backend also fails closed for out-of-scope Docs
 * (`GDRIVE_FOLDER_NOT_ALLOWED`), mapped below via
 * `friendlyGDriveImportErrorMessage`.
 *
 * Presentational note: a focused, on-brand "start a blog" screen — hero
 * (icon-in-circle + headline), a slim numbered "how it works" strip, and a
 * single prominent CTA. Container styling (`bg-white rounded-xl
 * shadow-card`) matches the creator PostTable benchmark.
 */

/** Numbered "how it works" steps shown above the picker CTA. */
const HOW_IT_WORKS_STEPS = [
  { icon: ICONS.FOLDER, key: 'blog_step_pick', defaultLabel: 'Pick a Google Doc' },
  { icon: ICONS.EYE, key: 'blog_step_preview', defaultLabel: 'Preview & edit' },
  { icon: ICONS.ROCKET, key: 'blog_step_publish', defaultLabel: 'Publish to WordPress' },
  { icon: ICONS.REMIX, key: 'blog_step_repurpose', defaultLabel: 'Repurpose to social' },
] as const;

export const BlogNew = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pickerError, setPickerError] = useState<string | null>(null);

  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  // Truthy check — `godModeSession` is `undefined` (not `null`) when absent,
  // so `!== null` would incorrectly treat every user as "in God Mode".
  const isGodMode = !!user?.godModeSession;

  // Drive is plan-gated separately from the blog: locked -> no background channel read, and the connect CTA
  // opens the upsell on click (a GET 403 is silent by design, so the click must explain itself).
  const gdriveLock = useLockedEntry('gdrive');
  const { data: gdriveChannels, isLoading: isLoadingChannels } = useGDriveChannels({ enabled: !gdriveLock.locked });
  // Prefer the ACTIVE channel the current user connected themselves — in a
  // team where several members each connect their own Google Drive, a naive
  // "first active channel" pick can scope the Picker (and the pull's token)
  // to the WRONG member's Drive. Falls back to the first active channel when
  // the current user hasn't connected one (single-connection teams, and
  // God-Mode staff viewing a team where only the owner connected).
  // Uses `connectedByUserId` (always populated, sourced from the channel's
  // `user_id`) rather than `createdBy` (currently always NULL on GDrive
  // channels — see GDriveChannel type comment).
  const activeGDriveChannel = useMemo(() => {
    const active = (gdriveChannels ?? []).filter((c) => c.isActive);
    return active.find((c) => currentUserId != null && c.connectedByUserId === currentUserId) ?? active[0];
  }, [gdriveChannels, currentUserId]);

  // The picked channel belongs to a TEAMMATE, not the current user — their
  // own Google Docs won't be visible to the connected account, so the pull
  // will 404. Gate the picker for everyone EXCEPT God-Mode staff, who
  // legitimately use the client team's shared connection.
  const usingTeammateChannel =
    !!activeGDriveChannel && currentUserId != null && activeGDriveChannel.connectedByUserId !== currentUserId;
  const mustConnectOwn = !isLoadingChannels && usingTeammateChannel && !isGodMode;

  // Access mode (docs/features/gdrive-folder-scoping/ACCESS-MODE.md
  // extension) — mirrors MediaSection.tsx. Always branch on `mode`, never on
  // `folders.length` (`mode: 'all'` also returns `folders: []`).
  // `'unconfigured'` blocks the Picker client-side (the backend also fails
  // closed for both the browse-scope check and the pulled Doc itself).
  const { data: gdriveFoldersData, isLoading: isLoadingGDriveFolders } = useGDriveFolders(
    activeGDriveChannel?.id ?? null
  );
  const gdriveSharedFolders = useMemo(() => gdriveFoldersData?.folders ?? [], [gdriveFoldersData]);
  const gdriveMode = gdriveFoldersData?.mode ?? 'unconfigured';
  const gdriveCanEditFolders = gdriveFoldersData?.canEdit ?? false;
  const [showGDriveFolderPrompt, setShowGDriveFolderPrompt] = useState(false);

  // One-time guard for the OAuth-return effect below — React re-renders
  // (incl. Strict Mode) must not re-fire the toast/invalidate a second time
  // for the same `?success=gdrive_connected` / `?error=` return.
  const gdriveReturnHandledRef = useRef(false);
  // Set right after a successful inline "connect Google Drive" OAuth return
  // (cdb #280) — tells the auto-open effect below to open the folder-config
  // prompt as soon as the newly-connected OWN channel appears as
  // `activeGDriveChannel` in an unconfigured, editable state. Consumed
  // (cleared) by that effect so it never re-fires later.
  const [awaitingNewGDriveChannel, setAwaitingNewGDriveChannel] = useState(false);

  // Handle the OAuth return from an inline "Connect Google Drive" click on
  // THIS page (cdb #280) — the backend callback redirects back here with
  // `?success=gdrive_connected&channelId=...&email=...` or `?error=...`
  // instead of the default `/settings/channels`.
  useEffect(() => {
    if (gdriveReturnHandledRef.current) return;
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    if (!success && !error) return;
    gdriveReturnHandledRef.current = true;

    if (success === 'gdrive_connected') {
      void queryClient.invalidateQueries({ queryKey: GDRIVE_KEYS.channels });
      toast.success(t('blog_gdrive_connected_toast', { defaultValue: 'Google Drive connected.' }));
      setAwaitingNewGDriveChannel(true);
    } else if (error) {
      toast.error(
        t('blog_gdrive_connect_error_toast', {
          defaultValue: 'Could not connect Google Drive: {{error}}',
          error,
        })
      );
    }

    const next = new URLSearchParams(searchParams);
    next.delete('success');
    next.delete('channelId');
    next.delete('email');
    next.delete('error');
    setSearchParams(next, { replace: true });
  }, [searchParams, queryClient, setSearchParams, t]);

  // Auto-open the folder-config prompt once the freshly-connected OWN
  // channel has settled in as `activeGDriveChannel` and its folder scope
  // has loaded as unconfigured+editable — lets the user pick their shared
  // folder immediately, without a second click.
  useEffect(() => {
    if (!awaitingNewGDriveChannel) return;
    if (isLoadingChannels || isLoadingGDriveFolders) return;
    if (!activeGDriveChannel) return;
    // Only for a channel the CURRENT user connected themselves — guards
    // against firing on a stale/teammate channel still active right after
    // invalidation, before the new channel has propagated into the list.
    if (currentUserId == null || activeGDriveChannel.connectedByUserId !== currentUserId) return;
    setAwaitingNewGDriveChannel(false);
    if (gdriveMode === 'unconfigured' && gdriveCanEditFolders) {
      setShowGDriveFolderPrompt(true);
    }
  }, [
    awaitingNewGDriveChannel,
    isLoadingChannels,
    isLoadingGDriveFolders,
    activeGDriveChannel,
    currentUserId,
    gdriveMode,
    gdriveCanEditFolders,
  ]);

  const { data: wpChannels } = useWordPressChannels();
  const activeWpChannels = (wpChannels ?? []).filter((c) => c.isActive);
  const singleWpChannel = activeWpChannels.length === 1 ? activeWpChannels[0] : undefined;

  const pullMutation = usePullBlogPost();

  const handlePull = useCallback(
    async (docId: string) => {
      try {
        const result = await pullMutation.mutateAsync({
          docInput: docId,
          gdriveChannelId: activeGDriveChannel?.id,
        });
        if (result.isExisting) {
          // Phase 3 re-pull dedupe guard — the same Google Doc was already
          // imported for this team; the server returned the EXISTING blog
          // instead of creating a duplicate. Route there instead of treating
          // this as a fresh draft.
          toast.info(
            t('blog_already_imported', {
              defaultValue: "You've already imported this Google Doc — opening it.",
            })
          );
        }
        navigate(`/blog/${result.blogPostId}`);
      } catch {
        // Error surfaced inline below via pullMutation.error
      }
    },
    [pullMutation, activeGDriveChannel?.id, navigate, t]
  );

  const handlePickerSelect = useCallback(
    (files: GDrivePickerFile[]) => {
      const doc = files[0];
      if (!doc) return;
      void handlePull(doc.id);
    },
    [handlePull]
  );

  const { open: openPicker, error: gdrivePickerError } = useGDrivePicker({
    channelId: activeGDriveChannel?.id ?? 0,
    onSelect: handlePickerSelect,
    // Restrict the Picker to native Google Docs — this is a Google Doc
    // import flow, not a media import flow (that's handled elsewhere).
    acceptMimeTypes: ['application/vnd.google-apps.document'],
    // Lock the Picker to the channel's shared folders (SPEC §5.2) — same
    // pattern as MediaSection's browse Picker.
    allowedFolders: gdriveSharedFolders,
    // mode:'all' (ACCESS-MODE extension) — whole Drive, no folder lock.
    unrestricted: gdriveMode === 'all',
  });

  // Inline "connect Google Drive" (cdb #280) — starts the OAuth flow with a
  // `returnTo` back to THIS page so the user never gets bounced to Settings.
  // The OAuth-return effect above picks up the `?success=gdrive_connected`
  // redirect and auto-opens the folder-config prompt.
  const handleConnectOwnGDrive = useCallback(async () => {
    if (gdriveLock.locked) {
      gdriveLock.showUpsell();
      return;
    }
    try {
      const authUrl = await channelsApi.connectGDrive(undefined, BLOG_NEW_ROUTE);
      window.location.href = authUrl;
    } catch (err) {
      // Safety-net fallback — e.g. NO_TEAM_CONTEXT (agency staff with no
      // resolvable home team) still needs the Settings team-picker flow; we
      // deliberately don't duplicate that picker here.
      navigate('/settings?tab=channels');
      toast.error(formatApiError(err).message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, gdriveLock.locked, gdriveLock.showUpsell]);

  const handleOpenPicker = async () => {
    setPickerError(null);
    if (isLoadingGDriveFolders) return; // avoid opening before folders resolve
    if (gdriveMode === 'unconfigured') {
      // UNCONFIGURED channel (SPEC §5.1/§5.2) — the pull fails closed
      // server-side regardless, so don't even open the browse Picker.
      if (gdriveCanEditFolders) {
        setShowGDriveFolderPrompt(true);
      } else {
        toast.info(
          t('gdrive_waiting_for_folders', {
            defaultValue: 'Waiting for the teammate who connected Google Drive to choose shared folders.',
          })
        );
      }
      return;
    }
    try {
      await openPicker();
    } catch (err) {
      setPickerError(err instanceof Error ? err.message : String(err));
    }
  };

  // Friendly message for the pull mutation's own error (e.g. the backend
  // rejecting an out-of-scope Doc with GDRIVE_FOLDER_NOT_ALLOWED even though
  // the Picker itself was correctly scoped — e.g. a stale allow-list).
  const pullErrorMessage = useMemo(() => {
    if (!pullMutation.error) return null;
    // A global upsell already explains a gating refusal: no second inline message.
    if (isGlobalGatingHandledError(pullMutation.error)) return null;
    const apiError = formatApiError(pullMutation.error);
    const baseMessage = apiError.code
      ? friendlyGDriveImportErrorMessage(apiError.code, apiError.message)
      : apiError.message;

    // Account-mismatch guard (UX) — GDOCS_ACCESS_DENIED is a clean, single-
    // cause signal (see comment on GDOCS_ACCESS_DENIED_CODE above): the
    // connected token couldn't read the picked Doc, most commonly because
    // the user signed into a different Google account in the Picker than
    // the one the Drive channel is connected as. Since this code cleanly
    // identifies that cause, swap in the account-mismatch copy instead of
    // the generic backend message. Every OTHER error code/status keeps its
    // existing message untouched, so we never show this misleading copy
    // for an unrelated failure.
    if (apiError.code === GDOCS_ACCESS_DENIED_CODE) {
      return t('blog_picker_pull_mismatch', {
        defaultValue:
          "We couldn't open that document with the connected Google account ({{email}}). Make sure you signed into that same account in the Google picker, then try again.",
        email: activeGDriveChannel?.accountEmail ?? 'the connected account',
      });
    }

    return baseMessage;
  }, [pullMutation.error, activeGDriveChannel?.accountEmail, t]);

  const noGDriveConnected = !isLoadingChannels && !activeGDriveChannel;

  return (
    <div>
      <BlogNewHeader />

      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-card p-6 sm:p-10">
          {noGDriveConnected ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <Icon name={ICONS.WARNING} size="md" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  {t('blog_no_gdrive_title', { defaultValue: 'Connect Google Drive first' })}
                </p>
                <p className="mt-1 text-sm text-amber-700">
                  {t('blog_no_gdrive_description', {
                    defaultValue: 'Pulling a blog post reads it from a Google Doc — connect a Google Drive account in Settings > Channels before continuing.',
                  })}
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={() => void handleConnectOwnGDrive()}
                >
                  {t('blog_connect_gdrive_action', { defaultValue: 'Connect Google Drive' })}
                </Button>
              </div>
            </div>
          ) : mustConnectOwn ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
              <Icon name={ICONS.WARNING} size="md" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  {t('blog_must_connect_own_title', { defaultValue: 'Connect your own Google Drive' })}
                </p>
                <p className="mt-1 text-sm text-amber-700">
                  {t('blog_must_connect_own_description', {
                    defaultValue:
                      'Google Drive access is tied to the account you connect. To pull your own documents, connect your own Google Drive — the shared folders are already shared with your email.',
                  })}
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={() => void handleConnectOwnGDrive()}
                >
                  {t('blog_connect_own_gdrive_action', { defaultValue: 'Connect my Google Drive' })}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Hero — icon-in-circle + confident headline */}
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 mb-4">
                  <Icon name={ICONS.BLOG} size="lg" color="text-primary-600" ariaHidden />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                  {t('blog_new_headline', { defaultValue: 'Turn a Google Doc into a published blog post' })}
                </h2>
                <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                  {t('blog_new_subcopy', {
                    defaultValue: "We'll pull the text and images from your Doc into a draft you can review before it goes live on WordPress.",
                  })}
                </p>
                {singleWpChannel && (
                  <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-3 py-1 text-xs text-gray-500">
                    <SiteFavicon iconUrl={singleWpChannel.siteIconUrl} siteName={singleWpChannel.siteUrl} size="xs" />
                    <span className="truncate max-w-[220px]">
                      {t('blog_new_publishing_to', {
                        defaultValue: 'Publishing to {{site}}',
                        site: singleWpChannel.siteUrl.replace(/^https?:\/\//, ''),
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* How it works — slim numbered strip */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {HOW_IT_WORKS_STEPS.map((step, index) => (
                  <div
                    key={step.key}
                    className="flex flex-col items-center text-center gap-1.5 rounded-lg bg-gray-50 px-2 py-3"
                  >
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center">
                        <Icon name={step.icon} size="sm" color="text-dark-teal" ariaHidden />
                      </div>
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-soft-mint text-dark-teal text-[10px] font-bold flex items-center justify-center">
                        {index + 1}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-gray-600 leading-tight">
                      {t(step.key, { defaultValue: step.defaultLabel })}
                    </span>
                  </div>
                ))}
              </div>

              {/* Primary CTA */}
              <div className="space-y-3">
                {activeGDriveChannel?.accountEmail && (
                  <p className="text-xs text-gray-500 text-center">
                    {t('blog_picker_account_hint', {
                      defaultValue:
                        'When Google asks you to sign in, choose {{email}}, the account this Drive is connected as.',
                      email: activeGDriveChannel.accountEmail,
                    })}
                  </p>
                )}
                <Button
                  variant="primary"
                  onClick={handleOpenPicker}
                  disabled={pullMutation.isPending || isLoadingGDriveFolders}
                  loading={pullMutation.isPending}
                  className="w-full flex items-center justify-center gap-2"
                >
                  {pullMutation.isPending ? (
                    t('blog_pulling', { defaultValue: 'Pulling document and images... this can take a moment' })
                  ) : (
                    <>
                      <Icon name={ICONS.FOLDER} size="sm" ariaHidden />
                      {t('blog_picker_button', { defaultValue: 'Choose a Google Doc' })}
                    </>
                  )}
                </Button>

                {(pickerError || gdrivePickerError) && (
                  <p className="text-xs text-red-600 text-center">
                    {pickerError || gdrivePickerError?.message}
                  </p>
                )}

                {pullMutation.isError && pullErrorMessage && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3">
                    <Icon name={ICONS.WARNING} size="sm" color="text-red-600" ariaHidden className="mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-700">{pullErrorMessage}</p>
                  </div>
                )}
              </div>

              {/* Folder-selection step (SPEC §5.1) — shown inline when the
                  connected GDrive channel is still UNCONFIGURED and the current
                  user can edit its scope. Same fail-closed prompt as
                  MediaSection. Deliberately NOT a modal/overlay — see
                  GDriveFolderPickerPanel.tsx's positioning-bug postmortem. */}
              {activeGDriveChannel && showGDriveFolderPrompt && (
                <GDriveFolderPickerPanel
                  open={showGDriveFolderPrompt}
                  onOpenChange={setShowGDriveFolderPrompt}
                  channelId={activeGDriveChannel.id}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogNew;
